import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Autocomplete
} from '@mui/material'
import {
  Save,
  Cancel,
  Add,
  Delete,
  Edit,
  DragHandle,
  Description,
  Timeline,
  Business,
  People,
  MenuBook,
  Gavel,
  AccountTree,
  SmartToy,
  HelpOutline
} from '@mui/icons-material'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import axios from 'axios'
import { useAuth } from '../../contexts/AuthContext'
import DiagramEditor from './DiagramEditor'
import DefinicionesAdmin from '../config/DefinicionesAdmin'
import ProcesoCreateModal from '../procesos/ProcesoCreateModal'
import AyudaProcedimiento from './AyudaProcedimiento'

const ProcedimientoForm = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [gestiones, setGestiones] = useState([])
  const [procesos, setProcesos] = useState([])
  
  // Formulario principal
  const [formData, setFormData] = useState({
    codigo: '',
    titulo: '',
    objetivo: '',
    alcance: '',
    proceso: null,
    version: '1.0',
    gestion_id: user?.gestion_id || '',
    responsables: [],
    definiciones: [],
    normatividades: [],
    normatividad_custom: ''
  })

  // Datos adicionales
  const [roles, setRoles] = useState([])
  const [definiciones, setDefiniciones] = useState([])
  const [normatividades, setNormatividades] = useState([])
  const [selectedGestion, setSelectedGestion] = useState(null)
  const [showCustomNormatividad, setShowCustomNormatividad] = useState(false)

  // Actividades
  const [actividades, setActividades] = useState([])
  const [openActivityDialog, setOpenActivityDialog] = useState(false)
  const [editingActivity, setEditingActivity] = useState(null)
  const [activityForm, setActivityForm] = useState({
    titulo: '',
    descripcion: '',
    informacion_soporte: '',
    informacion_generada: '',
    responsables: []
  })

  // Diagrama
  const [diagramCode, setDiagramCode] = useState('')
  const [diagramSvg, setDiagramSvg] = useState('')
  const [diagramHasUnsavedChanges, setDiagramHasUnsavedChanges] = useState(false)
  const [showUnsavedDiagramDialog, setShowUnsavedDiagramDialog] = useState(false)
  const diagramRef = useRef(null)

  // Modal Definiciones
  const [openDefinicionesDialog, setOpenDefinicionesDialog] = useState(false)

  // Modal Procesos
  const [openProcesosDialog, setOpenProcesosDialog] = useState(false)

  // Modal Ayuda
  const [openAyuda, setOpenAyuda] = useState(false)

  // Procedimiento temporal para el diagrama
  const [tempProcedimientoId, setTempProcedimientoId] = useState(null)

  useEffect(() => {
    loadGestiones()
    loadRoles()
    loadDefiniciones()
    loadNormatividades()
    loadProcesos()

    // NO crear procedimiento temporal para nuevos procedimientos
    // El DiagramEditor trabajará con datos locales usando generateWithLocalAI()

    // Configurar auto-recarga cada 30 segundos para las opciones de los selects
    const interval = setInterval(() => {
      console.log('🔄 Auto-recargando opciones de selects...')
      loadRoles()
      loadDefiniciones()
      loadNormatividades()
      loadProcesos()
    }, 30000) // 30 segundos

    // Cleanup interval al desmontar el componente
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (formData.gestion_id && gestiones.length > 0) {
      const gestion = gestiones.find(g => g.id == formData.gestion_id)
      setSelectedGestion(gestion)
    } else {
      setSelectedGestion(null)
      setCodigoInput('')
      setFormData(prev => ({ ...prev, codigo: '' }))
    }
  }, [formData.gestion_id, gestiones])

  const loadGestiones = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get('/api/gestiones?limit=100', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setGestiones(response.data.filter(g => g.activa))
    } catch (error) {
      setError('Error al cargar gestiones: ' + (error.response?.data?.detail || error.message))
    }
  }

  const loadRoles = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get('/api/roles-responsabilidad', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setRoles(response.data)
    } catch (error) {
      console.error('Error al cargar roles:', error)
    }
  }

  const loadDefiniciones = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get('/api/definiciones', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setDefiniciones(response.data)
    } catch (error) {
      console.error('Error al cargar definiciones:', error)
    }
  }

  const loadNormatividades = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get('/api/normatividad', {
        headers: { Authorization: `Bearer ${token}` }
      })
      // Agregar opción "otra" al final de la lista
      const normatividades = response.data || []
      const normatividadesConOtra = [
        ...normatividades,
        { id: 'otra', titulo: 'Otra (especificar manualmente)', categoria: 'otra' }
      ]
      setNormatividades(normatividadesConOtra)
    } catch (error) {
      console.error('Error al cargar normatividades:', error)
      // Si hay error, al menos tener la opción "otra"
      setNormatividades([{ id: 'otra', titulo: 'Otra (especificar manualmente)', categoria: 'otra' }])
    }
  }

  const loadProcesos = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get('/api/procesos?activo=true&limit=100', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setProcesos(response.data || [])
    } catch (error) {
      console.error('Error al cargar procesos:', error)
      setProcesos([])
    }
  }

  // Funciones para recargar datos específicos cuando el usuario interactúa
  const handleRefreshRoles = async () => {
    console.log('🔄 Recargando roles por interacción del usuario...')
    await loadRoles()
  }

  const handleRefreshDefiniciones = async () => {
    console.log('🔄 Recargando definiciones por interacción del usuario...')
    await loadDefiniciones()
  }

  const handleRefreshNormatividades = async () => {
    console.log('🔄 Recargando normatividades por interacción del usuario...')
    await loadNormatividades()
  }

  const handleRefreshProcesos = async () => {
    console.log('🔄 Recargando procesos por interacción del usuario...')
    await loadProcesos()
  }

  const [codigoInput, setCodigoInput] = useState('')

  const handleCodigoChange = (e) => {
    const input = e.target.value
    setCodigoInput(input)
    
    if (selectedGestion && input) {
      setFormData(prev => ({ ...prev, codigo: `${selectedGestion.codigo}-${input}` }))
    } else {
      setFormData(prev => ({ ...prev, codigo: '' }))
    }
  }

  const handleNormatividadChange = (event, newValue) => {
    // Verificar si se seleccionó "otra"
    const hasOtra = newValue.some(norm => norm.id === 'otra')
    setShowCustomNormatividad(hasOtra)
    
    setFormData(prev => ({ ...prev, normatividades: newValue }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Verificar si hay diagrama renderizado sin guardar
    if (diagramHasUnsavedChanges) {
      setShowUnsavedDiagramDialog(true)
      return
    }

    await submitProcedimiento()
  }

  const submitProcedimiento = async () => {
    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      
      // Validaciones antes de enviar
      if (!formData.titulo || formData.titulo.trim().length < 5) {
        setError('El título debe tener al menos 5 caracteres')
        setLoading(false)
        return
      }
      if (!formData.proceso) {
        setError('Debe seleccionar un proceso')
        setLoading(false)
        return
      }
      if (!formData.objetivo || formData.objetivo.trim().length < 10) {
        setError('El objetivo debe tener al menos 10 caracteres')
        setLoading(false)
        return
      }
      if (!formData.alcance || formData.alcance.trim().length < 10) {
        setError('El alcance debe tener al menos 10 caracteres')
        setLoading(false)
        return
      }
      if (!formData.gestion_id) {
        setError('Debe seleccionar una gestión')
        setLoading(false)
        return
      }
      if (!formData.codigo || formData.codigo.trim().length < 3) {
        setError('El código debe tener al menos 3 caracteres')
        setLoading(false)
        return
      }
      
      // Limpiar y validar datos antes de enviar
      const cleanFormData = {
        codigo: formData.codigo.trim(),
        titulo: formData.titulo.trim(),
        proceso: formData.proceso?.nombre || formData.proceso,
        objetivo: formData.objetivo.trim(),
        alcance: formData.alcance.trim(),
        version: formData.version || '1.0',
        gestion_id: parseInt(formData.gestion_id),
        // Asegurar que responsables es array de objetos con estructura correcta
        responsables: Array.isArray(formData.responsables) 
          ? formData.responsables.map(resp => ({ id: resp.id }))
          : [],
        // Convertir definiciones array a string para backend
        definiciones: Array.isArray(formData.definiciones) 
          ? formData.definiciones.map(def => def.termino).join(', ')
          : '',
        // Procesar normatividades seleccionadas
        normatividad: Array.isArray(formData.normatividades) 
          ? formData.normatividades
              .filter(norm => norm.id !== 'otra')
              .map(norm => norm.titulo)
              .concat(formData.normatividad_custom ? [formData.normatividad_custom] : [])
              .join(', ')
          : formData.normatividad_custom || ''
      }
      
      console.log('Datos a enviar:', cleanFormData)
      
      // Crear procedimiento
      const response = await axios.post('/api/procedimientos', cleanFormData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      const procedimientoId = response.data.id

      // Crear actividades si existen
      if (actividades.length > 0) {
        for (const [index, actividad] of actividades.entries()) {
          await axios.post(`/api/procedimientos/${procedimientoId}/actividades`, {
            ...actividad,
            orden: index + 1
          }, {
            headers: { Authorization: `Bearer ${token}` }
          })
        }
      }

      // Crear diagrama si existe
      if (diagramCode.trim()) {
        try {
          await axios.post(`/api/procedimientos/${procedimientoId}/diagrama`, {
            mermaid_code: diagramCode,
            svg_rendered: diagramSvg || '',
            auto_generated: false
          }, {
            headers: { Authorization: `Bearer ${token}` }
          })
        } catch (diagramError) {
          console.warn('Error al guardar diagrama:', diagramError)
          // No fallar por el diagrama, solo avisar
        }
      }

      // Limpiar procedimiento temporal si existe
      if (tempProcedimientoId) {
        try {
          await axios.delete(`/api/procedimientos/${tempProcedimientoId}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          console.log('Procedimiento temporal eliminado:', tempProcedimientoId)
        } catch (tempError) {
          console.warn('Error al eliminar procedimiento temporal:', tempError)
          // No fallar por esto
        }
      }

      setSuccess('Procedimiento creado exitosamente. Redirigiendo al editor de diagramas...')
      // Redirigir inmediatamente a editar el procedimiento con foco en diagrama
      setTimeout(() => navigate(`/procedimientos/${procedimientoId}/editar?focus=diagrama`), 1500)
      
    } catch (error) {
      console.error('Error completo:', error)
      let errorMessage = 'Error al crear procedimiento: '
      
      if (error.response?.status === 422) {
        // Error de validación
        if (error.response.data?.detail) {
          if (Array.isArray(error.response.data.detail)) {
            // Errores de validación de Pydantic
            errorMessage += error.response.data.detail.map(err => 
              `${err.loc?.join('.')} - ${err.msg}`
            ).join('; ')
          } else {
            errorMessage += error.response.data.detail
          }
        } else {
          errorMessage += 'Datos inválidos'
        }
      } else {
        errorMessage += error.response?.data?.detail || error.message
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleActivitySubmit = () => {
    if (editingActivity !== null) {
      // Editar actividad existente
      const newActividades = [...actividades]
      newActividades[editingActivity] = { ...activityForm }
      setActividades(newActividades)
    } else {
      // Agregar nueva actividad
      setActividades([...actividades, { ...activityForm }])
    }
    
    // Resetear formulario
    setActivityForm({
      titulo: '',
      descripcion: '',
      informacion_soporte: '',
      informacion_generada: '',
      responsables: []
    })
    setEditingActivity(null)
    setOpenActivityDialog(false)
  }

  const handleEditActivity = (index) => {
    setEditingActivity(index)
    setActivityForm({ ...actividades[index] })
    setOpenActivityDialog(true)
  }

  const handleDeleteActivity = (index) => {
    const newActividades = actividades.filter((_, i) => i !== index)
    setActividades(newActividades)
  }

  const handleDragEnd = (result) => {
    if (!result.destination) return

    const newActividades = Array.from(actividades)
    const [reorderedItem] = newActividades.splice(result.source.index, 1)
    newActividades.splice(result.destination.index, 0, reorderedItem)
    
    setActividades(newActividades)
  }

  const openNewActivityDialog = () => {
    setEditingActivity(null)
    setActivityForm({
      titulo: '',
      descripcion: '',
      informacion_soporte: '',
      informacion_generada: '',
      responsables: []
    })
    setOpenActivityDialog(true)
  }

  const handleDiagramSave = async (code, svgContent) => {
    try {
      // Guardar en el estado local
      setDiagramCode(code)
      setDiagramSvg(svgContent)
      setDiagramHasUnsavedChanges(false)

      setSuccess('Diagrama guardado localmente. Recuerde guardar el procedimiento para confirmar los cambios.')
      setTimeout(() => setSuccess(null), 3000)

      // Si existe procedimiento temporal, guardar en el servidor también
      if (tempProcedimientoId) {
        const token = localStorage.getItem('token')
        await axios.post(`/api/procedimientos/${tempProcedimientoId}/diagrama`, {
          mermaid_code: code,
          svg_rendered: svgContent,
          auto_generated: false
        }, {
          headers: { Authorization: `Bearer ${token}` }
        })
      }
    } catch (error) {
      setError('Error al guardar diagrama: ' + (error.response?.data?.detail || error.message))
    }
  }

  // Función para crear procedimiento temporal para el diagrama
  const createTempProcedimiento = async () => {
    try {
      const token = localStorage.getItem('token')

      const tempData = {
        codigo: `TEMP_${Date.now()}`,
        titulo: formData.titulo || 'Procedimiento Temporal',
        objetivo: formData.objetivo || 'Temporal',
        alcance: formData.alcance || 'Temporal',
        proceso: formData.proceso || 'Temporal',
        version: '0.0.1',
        gestion_id: formData.gestion_id || user?.gestion_id,
        estado: 'borrador',
        responsables: [],
        definiciones: [],
        normatividades: []
      }

      const response = await axios.post('/api/procedimientos', tempData, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.data && response.data.id) {
        setTempProcedimientoId(response.data.id)
        console.log('Procedimiento temporal creado:', response.data.id)
        return response.data.id
      }
    } catch (error) {
      console.error('Error creando procedimiento temporal:', error)
      setError('Error al crear procedimiento temporal para el diagrama')
    }
    return null
  }

  // Función para abrir modal de definiciones
  const handleOpenDefiniciones = () => {
    setOpenDefinicionesDialog(true)
  }

  const handleDefinicionesClose = async () => {
    setOpenDefinicionesDialog(false)
    // Recargar definiciones después de cerrar el modal
    await loadDefiniciones()
  }

  // Función para abrir modal de procesos
  const handleOpenProcesos = () => {
    setOpenProcesosDialog(true)
  }

  const handleProcesosClose = async () => {
    setOpenProcesosDialog(false)
    // Recargar procesos después de cerrar el modal
    await loadProcesos()
  }

  const handleProcesoCreated = async (nuevoProceso) => {
    // Recargar procesos después de crear uno nuevo
    await loadProcesos()
    // Seleccionar automáticamente el proceso recién creado
    setFormData(prev => ({ ...prev, proceso: nuevoProceso }))
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Description sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Crear Nuevo Procedimiento
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<HelpOutline />}
            onClick={() => setOpenAyuda(true)}
            color="info"
          >
            Ayuda
          </Button>
          <Button
            variant="outlined"
            startIcon={<Cancel />}
            onClick={() => navigate('/procedimientos')}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit}
            disabled={loading || actividades.length === 0}
          >
            {loading ? 'Guardando...' : actividades.length === 0 ? 'Agrega al menos una actividad' : 'Guardar y Crear Diagrama'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Información General */}
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="Información General"
                avatar={<Business />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth required>
                      <InputLabel>Gestión</InputLabel>
                      <Select
                        value={formData.gestion_id}
                        label="Gestión"
                        onChange={(e) => setFormData({ ...formData, gestion_id: e.target.value })}
                      >
                        {gestiones.map((gestion) => (
                          <MenuItem key={gestion.id} value={gestion.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  backgroundColor: gestion.color || '#64B5F6'
                                }}
                              />
                              <Typography>{gestion.codigo} - {gestion.nombre}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Código del Procedimiento"
                      value={codigoInput}
                      onChange={handleCodigoChange}
                      placeholder={selectedGestion ? "0101" : "Seleccione gestión primero"}
                      disabled={!selectedGestion}
                      required
                      InputProps={{
                        startAdornment: selectedGestion ? (
                          <Typography variant="body2" sx={{ mr: 1, color: 'primary.main', fontWeight: 'bold' }}>
                            {selectedGestion.codigo}-
                          </Typography>
                        ) : null,
                      }}
                      helperText={selectedGestion ? `Código completo: ${formData.codigo || selectedGestion.codigo + '-'}` : ''}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Versión"
                      value={formData.version}
                      onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Título del Procedimiento"
                      value={formData.titulo}
                      onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                      placeholder="Título descriptivo del procedimiento"
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Card sx={{ mb: 2 }}>
                      <CardHeader
                        title="Proceso"
                        avatar={<AccountTree />}
                        action={
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Add />}
                            onClick={handleOpenProcesos}
                            color="primary"
                          >
                            Nuevo Proceso
                          </Button>
                        }
                      />
                      <CardContent>
                        <Autocomplete
                          options={procesos}
                          getOptionLabel={(option) => option.nombre || ''}
                          value={formData.proceso}
                          onChange={(event, newValue) => {
                            setFormData({ ...formData, proceso: newValue })
                          }}
                          onFocus={handleRefreshProcesos}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Proceso"
                              placeholder="Seleccionar proceso al que pertenece..."
                              required
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body1">
                                  {option.nombre}
                                </Typography>
                                {option.descripcion && (
                                  <Typography variant="body2" color="text.secondary">
                                    {option.descripcion}
                                  </Typography>
                                )}
                                {option.gestion_nombre && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: option.gestion_color || '#64B5F6'
                                      }}
                                    />
                                    <Typography variant="caption" sx={{ color: option.gestion_color || '#64B5F6' }}>
                                      Gestión: {option.gestion_codigo} - {option.gestion_nombre}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </li>
                          )}
                          noOptionsText="No hay procesos disponibles"
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Objetivo"
                      multiline
                      rows={3}
                      value={formData.objetivo}
                      onChange={(e) => setFormData({ ...formData, objetivo: e.target.value })}
                      placeholder="Objetivo principal del procedimiento"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Alcance"
                      multiline
                      rows={3}
                      value={formData.alcance}
                      onChange={(e) => setFormData({ ...formData, alcance: e.target.value })}
                      placeholder="Alcance y limitaciones del procedimiento"
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={normatividades}
                      getOptionLabel={(option) => option.titulo}
                      value={formData.normatividades}
                      onChange={handleNormatividadChange}
                      onFocus={handleRefreshNormatividades}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Normatividad"
                          placeholder="Seleccionar marco normativo aplicable..."
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option.titulo}
                            {...getTagProps({ index })}
                            key={option.id}
                            color={option.id === 'otra' ? 'secondary' : 'default'}
                          />
                        ))
                      }
                    />
                    {showCustomNormatividad && (
                      <TextField
                        fullWidth
                        sx={{ mt: 2 }}
                        label="Especificar Normatividad"
                        multiline
                        rows={2}
                        value={formData.normatividad_custom}
                        onChange={(e) => setFormData({ ...formData, normatividad_custom: e.target.value })}
                        placeholder="Ingrese la normatividad específica..."
                        helperText="Escriba la normatividad que no aparece en la lista predefinida"
                      />
                    )}
                    {formData.normatividades.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Normatividad seleccionada:
                        </Typography>
                        {formData.normatividades.filter(norm => norm.id !== 'otra').map((norm) => (
                          <Box key={norm.id} sx={{ mb: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {norm.titulo}
                            </Typography>
                            {norm.numero && (
                              <Typography variant="body2" color="text.secondary">
                                Número: {norm.numero}
                              </Typography>
                            )}
                            {norm.entidad_emisora && (
                              <Typography variant="body2" color="text.secondary">
                                Entidad emisora: {norm.entidad_emisora}
                              </Typography>
                            )}
                          </Box>
                        ))}
                        {formData.normatividad_custom && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              Normatividad personalizada:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formData.normatividad_custom}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Responsables */}
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="Responsables"
                avatar={<People />}
              />
              <CardContent>
                <Autocomplete
                  multiple
                  options={roles}
                  getOptionLabel={(option) => option.nombre}
                  value={formData.responsables}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, responsables: newValue })
                  }}
                  onFocus={handleRefreshRoles}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seleccionar Responsables"
                      placeholder="Buscar roles de responsabilidad..."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.nombre}
                        {...getTagProps({ index })}
                        key={option.id}
                      />
                    ))
                  }
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Definiciones */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Definiciones"
                avatar={<MenuBook />}
                action={
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={handleOpenDefiniciones}
                    color="primary"
                  >
                    Nueva Definición
                  </Button>
                }
              />
              <CardContent>
                <Autocomplete
                  multiple
                  options={definiciones}
                  getOptionLabel={(option) => option.termino}
                  value={formData.definiciones}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, definiciones: newValue })
                  }}
                  onFocus={handleRefreshDefiniciones}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seleccionar Definiciones"
                      placeholder="Buscar definiciones aplicables..."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.termino}
                        {...getTagProps({ index })}
                        key={option.id}
                        title={option.definicion}
                      />
                    ))
                  }
                />
                {formData.definiciones.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Definiciones seleccionadas:
                    </Typography>
                    {formData.definiciones.map((def) => (
                      <Box key={def.id} sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {def.termino}:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {def.definicion}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Actividades */}
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="Actividades del Procedimiento"
                avatar={<Timeline />}
                action={
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    onClick={openNewActivityDialog}
                  >
                    Agregar Actividad
                  </Button>
                }
              />
              <CardContent>
                {actividades.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No hay actividades definidas. Agrega al menos una actividad para el procedimiento.
                    </Typography>
                  </Box>
                ) : (
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="actividades">
                      {(provided) => (
                        <List {...provided.droppableProps} ref={provided.innerRef}>
                          {actividades.map((actividad, index) => (
                            <Draggable key={index} draggableId={`activity-${index}`} index={index}>
                              {(provided, snapshot) => (
                                <ListItem
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  sx={{
                                    mb: 1,
                                    bgcolor: snapshot.isDragging ? 'action.hover' : 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1
                                  }}
                                >
                                  <Box
                                    {...provided.dragHandleProps}
                                    sx={{ mr: 2, display: 'flex', alignItems: 'center' }}
                                  >
                                    <DragHandle />
                                  </Box>
                                  <Box sx={{ flex: 1, ml: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                      <Chip label={`${index + 1}`} size="small" color="primary" />
                                      <Typography variant="subtitle2" fontWeight="bold">
                                        {actividad.titulo}
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                      {actividad.descripcion}
                                    </Typography>
                                    {actividad.responsables && actividad.responsables.length > 0 && (
                                      <Box>
                                        <Typography variant="caption" color="primary" sx={{ mr: 1 }}>
                                          Responsables: 
                                        </Typography>
                                        {actividad.responsables.map((resp, idx) => (
                                          <Chip 
                                            key={idx} 
                                            label={resp.nombre} 
                                            size="small" 
                                            variant="outlined"
                                            color="primary"
                                            sx={{ mr: 0.5 }}
                                          />
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                  <ListItemSecondaryAction>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditActivity(index)}
                                      sx={{ mr: 1 }}
                                    >
                                      <Edit />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteActivity(index)}
                                      color="error"
                                    >
                                      <Delete />
                                    </IconButton>
                                  </ListItemSecondaryAction>
                                </ListItem>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </List>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Diagrama de Flujo - OCULTO EN NUEVO PROCEDIMIENTO */}
          {/* El diagrama se editará en la vista de edición después de guardar */}
          {false && (
            <Grid item xs={12}>
              <Card ref={diagramRef}>
                <CardHeader
                  title="Diagrama de Flujo (Flujograma)"
                  avatar={<AccountTree />}
                />
                <CardContent>
                  <DiagramEditor
                    initialCode={diagramCode}
                    onSave={handleDiagramSave}
                    onChange={(code, svg, hasUnsavedChanges) => {
                      setDiagramCode(code)
                      setDiagramSvg(svg)
                      setDiagramHasUnsavedChanges(hasUnsavedChanges)
                    }}
                    actividades={actividades}
                    procedimientoId={tempProcedimientoId}
                    procedimientoData={formData}
                    readOnly={false}
                  />
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </form>

      {/* Dialog para Definiciones */}
      <Dialog
        open={openDefinicionesDialog}
        onClose={handleDefinicionesClose}
        maxWidth="lg"
        fullWidth
      >
        <DefinicionesAdmin />
      </Dialog>

      {/* Modal para crear nuevo proceso */}
      <ProcesoCreateModal
        open={openProcesosDialog}
        onClose={handleProcesosClose}
        onSuccess={handleProcesoCreated}
        defaultGestionId={formData.gestion_id}
        gestiones={gestiones}
      />

      {/* Dialog para Actividades */}
      <Dialog open={openActivityDialog} onClose={() => setOpenActivityDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingActivity !== null ? 'Editar Actividad' : 'Nueva Actividad'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Título de la Actividad"
              value={activityForm.titulo}
              onChange={(e) => setActivityForm({ ...activityForm, titulo: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Descripción de la Actividad"
              multiline
              rows={3}
              value={activityForm.descripcion}
              onChange={(e) => setActivityForm({ ...activityForm, descripcion: e.target.value })}
              placeholder="Descripción detallada de la actividad"
              required
            />
            <TextField
              fullWidth
              label="Información de Soporte"
              multiline
              rows={2}
              value={activityForm.informacion_soporte}
              onChange={(e) => setActivityForm({ ...activityForm, informacion_soporte: e.target.value })}
              placeholder="Documentos, sistemas, recursos necesarios para ejecutar la actividad"
            />
            <TextField
              fullWidth
              label="Información Generada"
              multiline
              rows={2}
              value={activityForm.informacion_generada}
              onChange={(e) => setActivityForm({ ...activityForm, informacion_generada: e.target.value })}
              placeholder="Documentos, reportes, registros que genera esta actividad"
            />
            <Autocomplete
              multiple
              options={formData.responsables}
              getOptionLabel={(option) => option.nombre}
              value={activityForm.responsables}
              onChange={(event, newValue) => {
                setActivityForm({ ...activityForm, responsables: newValue })
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Responsables de la Actividad"
                  placeholder="Seleccionar responsables..."
                  helperText="Solo se pueden asignar responsables ya definidos en el procedimiento"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.nombre}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
              disabled={formData.responsables.length === 0}
            />
            {formData.responsables.length === 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Primero debe definir los responsables del procedimiento en la sección "Responsables" para poder asignarlos a las actividades.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenActivityDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleActivitySubmit} 
            variant="contained"
            disabled={!activityForm.titulo || !activityForm.descripcion}
          >
            {editingActivity !== null ? 'Actualizar' : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Ayuda */}
      <AyudaProcedimiento
        open={openAyuda}
        onClose={() => setOpenAyuda(false)}
      />

      {/* Dialog de Diagrama No Guardado */}
      <Dialog
        open={showUnsavedDiagramDialog}
        onClose={() => setShowUnsavedDiagramDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          ⚠️ Diagrama sin guardar
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Ha generado o editado un diagrama pero no lo ha guardado.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Si continúa sin guardar el diagrama, solo se guardará el código Mermaid pero no la visualización renderizada (SVG),
            lo que significa que el diagrama no se mostrará en el PDF ni en la vista previa del procedimiento.
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold' }}>
            ¿Desea guardar el diagrama antes de continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowUnsavedDiagramDialog(false)
              submitProcedimiento()
            }}
            color="error"
          >
            Continuar sin guardar diagrama
          </Button>
          <Button
            onClick={() => {
              setShowUnsavedDiagramDialog(false)
              // Hacer scroll al diagrama con un pequeño delay para que el dialog se cierre
              setTimeout(() => {
                diagramRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                })
              }, 100)
            }}
            variant="contained"
            autoFocus
          >
            Volver a guardar diagrama
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProcedimientoForm